// ProductionUnitsMobile.jsx — onglet "Unités de production" (mobile)
// À copier dans : src/GestionProductions/ProductionUnitsMobile.jsx
// Mêmes endpoints que ProductionList.js (getProductionUnits / delete / duplicate).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { message, Modal } from "antd";
import {
  SearchOutlined, BellOutlined, SlidersOutlined, PlusOutlined, RightOutlined,
  ExperimentOutlined, AppstoreOutlined, RocketOutlined, EditOutlined, CopyOutlined,
  DeleteOutlined, CloseOutlined, InboxOutlined, LoadingOutlined,
} from "@ant-design/icons";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  getProductionUnits, deleteProductionUnit, duplicateProductionUnit,
} from "./api";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import { getSelectedProductionTerminology } from "./productionTerminology";
import MobileModuleHeader, { MobileHeaderAction } from "../mobile/MobileModuleHeader";
import "./production-mobile-kit.css";
import "./production-units.css";

const SEMI = "semi_finished";
const PERM = {
  view: "Gestion Commerciale.Approvisionnement.Production.Unites.view",
  create: "Gestion Commerciale.Approvisionnement.Production.Unites.create",
  edit: "Gestion Commerciale.Approvisionnement.Production.Unites.edit",
  delete: "Gestion Commerciale.Approvisionnement.Production.Unites.delete",
  manage: "Gestion Commerciale.Approvisionnement.Production.Gerer.view",
};
const fmtNum = (v, d = 0) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: d }).format(Number(v) || 0);
const fmtF = (v) => fmtNum(Math.round(Number(v) || 0)) + " F";

export default function ProductionUnitsMobile({ onBack }) {
  const navigate = useNavigate();
  const outletContext = useOutletContext();
  const { hasPermission, user } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const t = getSelectedProductionTerminology(user, selectedCompany);
  const can = {
    create: hasPermission(PERM.create), edit: hasPermission(PERM.edit),
    delete: hasPermission(PERM.delete), manage: hasPermission(PERM.manage),
  };
  const searchRef = useRef(null);
  const focusSearch = () => {
    searchRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    searchRef.current?.focus();
  };
  const goNotifications = () => navigate("/notifications");

  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sheetUnit, setSheetUnit] = useState(null);

  const load = () => {
    if (!selectedWarehouse) { setUnits([]); return; }
    setLoading(true);
    getProductionUnits({ page: 1, limit: 200, warehouse_id: selectedWarehouse, company_id: selectedCompany })
      .then((data) =>
        setUnits(
          (data?.units || []).filter(
            (u) => Number(u.warehouse_id) === Number(selectedWarehouse)
          )
        )
      )
      .catch(() => message.error(`Erreur lors du chargement des ${t.unitPlural}`))
      .finally(() => setLoading(false));
  };
  useEffect(load, [selectedWarehouse, selectedCompany]); // eslint-disable-line

  const stats = useMemo(() => ({
    total: units.length,
    active: units.filter((u) => u.status === "active").length,
    materials: units.reduce((s, u) => s + (Number(u.materials_count) || 0), 0),
  }), [units]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return units.filter((u) => {
      if (filter === "finished" && u.production_kind === SEMI) return false;
      if (filter === "semi" && u.production_kind !== SEMI) return false;
      if (filter === "active" && u.status !== "active") return false;
      if (q && !String(u.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [units, query, filter]);

  const produce = (u) => { setSheetUnit(null); navigate(`/production/process/${u.id}`); };
  const edit = (u) => { setSheetUnit(null); navigate(`/production/units/edit/${u.id}`); };
  const duplicate = async (u) => {
    setSheetUnit(null);
    try { await duplicateProductionUnit(u.id); message.success(`${t.unitSingularTitle} dupliquée`); load(); }
    catch (e) { message.error(e?.error || "Erreur lors de la duplication"); }
  };
  const askDelete = (u) => {
    setSheetUnit(null);
    Modal.confirm({
      title: `Supprimer cette ${t.unitSingular} ?`, okText: "Oui, supprimer", okType: "danger",
      cancelText: "Non", centered: true,
      content: "Cette action pourrait affecter les productions associées.",
      onOk: async () => {
        try { await deleteProductionUnit(u.id); message.success(`${t.unitSingularTitle} supprimée`); load(); }
        catch (e) { message.error(e?.error || "Erreur lors de la suppression"); }
      },
    });
  };

  if (!selectedWarehouse) {
    return (
      <div className="pm-root production-units-mobile">
        <Header t={t} onBack={onBack} onMenu={outletContext?.openMobileMenu} onBell={goNotifications} />
        <div className="pm-body"><div className="pm-state">
          <div className="pm-state-badge"><InboxOutlined /></div>
          <h3 className="pm-state-title">Aucun établissement sélectionné</h3>
          <p className="pm-state-text">Choisissez un établissement pour voir ses {t.unitPlural}.</p>
        </div></div>
      </div>
    );
  }

  return (
    <div className="pm-root production-units-mobile">
      <Header t={t} onBack={onBack} onMenu={outletContext?.openMobileMenu} onSearch={focusSearch} onBell={goNotifications} />
      <div className="pm-body">
        <div className="pm-summary">
          {[{ v: stats.total, l: t.unitsTitleShort }, { v: stats.active, l: "Actives" }, { v: stats.materials, l: t.materialPluralTitle }].map((it, i) => (
            <React.Fragment key={it.l}>
              {i > 0 && <div className="pm-summary-div" />}
              <div className="pm-summary-cell"><span className="pm-summary-val">{fmtNum(it.v)}</span><span className="pm-summary-lbl">{it.l}</span></div>
            </React.Fragment>
          ))}
        </div>

        <div className="pm-toolbar">
          <div className="pm-search">
            <SearchOutlined />
            <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Rechercher une ${t.unitSingular}…`} />
          </div>
          <button className="pm-iconbtn-solid"><SlidersOutlined /></button>
        </div>

        <div className="pm-chips">
          {[{ k: "all", l: "Toutes" }, { k: "finished", l: "Produits finis" }, { k: "semi", l: "Semi-finis" }, { k: "active", l: "Actives" }].map((f) => (
            <button key={f.k} className={`pm-chip-f ${filter === f.k ? "is-active" : ""}`} onClick={() => setFilter(f.k)}>{f.l}</button>
          ))}
        </div>

        {can.manage && (
          <button
            className="pm-btn pm-btn--primary pm-btn--block"
            style={{ marginBottom: 14 }}
            onClick={() => navigate("/production/process/batch")}
          >
            <RocketOutlined />
            Lancer plusieurs {t.costContext === "cuisine" ? "preparations" : "productions"}
          </button>
        )}

        {loading ? (
          <div className="pm-spin"><LoadingOutlined /></div>
        ) : visible.length === 0 ? (
          <div className="pm-state"><div className="pm-state-badge"><ExperimentOutlined /></div>
            <h3 className="pm-state-title">{query || filter !== "all" ? "Aucun résultat" : t.noUnitFound}</h3>
            <p className="pm-state-text">Créez une {t.unitSingular} pour commencer à produire.</p></div>
        ) : (
          <div className="pm-list">
            {visible.map((u) => (
              <button key={u.id} className="pm-ucard" onClick={() => setSheetUnit(u)}>
                <div className="pm-iconchip" style={{ width: 46, height: 46, fontSize: 22 }}>
                  {u.production_kind === SEMI ? <AppstoreOutlined /> : <ExperimentOutlined />}
                </div>
                <div className="pm-ucard-mid">
                  <div className="pm-ucard-name">{u.name}</div>
                  <div className="pm-ucard-meta">
                    <KindPill kind={u.production_kind} />
                    {Number(u.materials_count) > 0 && <span className="pm-chip">{u.materials_count} {t.materialPlural}</span>}
                    {u.status !== "active" && <span className="pm-pill pm-pill--sm pm-pill--amber">Inactive</span>}
                  </div>
                </div>
                <div className="pm-ucard-right">
                  {Number(u.default_output_quantity) > 0
                    ? <><div className="pm-ucard-right-v">{fmtNum(u.default_output_quantity)} {u.unit_short_name || ""}</div><div className="pm-ucard-right-l">par lot</div></>
                    : <RightOutlined style={{ color: "var(--pm-muted-2)" }} />}
                </div>
              </button>
            ))}
          </div>
        )}
        <div style={{ height: 90 }} />
      </div>

      {can.create && <button className="pm-fab" onClick={() => navigate("/production/units/create")}><PlusOutlined />Nouvelle</button>}

      {/* feuille d'actions */}
      {sheetUnit && (
        <>
          <div className="pm-scrim" onClick={() => setSheetUnit(null)} />
          <div className="pm-sheet">
            <div className="pm-grab" />
            <div className="pm-sheet-head">
              <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
                <div className="pm-iconchip" style={{ width: 46, height: 46, fontSize: 22 }}>
                  {sheetUnit.production_kind === SEMI ? <AppstoreOutlined /> : <ExperimentOutlined />}
                </div>
                <div>
                  <h2 className="pm-sheet-title" style={{ fontSize: 17 }}>{sheetUnit.name}</h2>
                  <div style={{ marginTop: 5 }}><KindPill kind={sheetUnit.production_kind} /></div>
                </div>
              </div>
              <button className="pm-iconbtn" onClick={() => setSheetUnit(null)}><CloseOutlined /></button>
            </div>
            {can.manage && <button className="pm-action-row" onClick={() => produce(sheetUnit)}><span className="pm-action-ico"><RocketOutlined /></span>Lancer une {t.costContext}</button>}
            {can.edit && <button className="pm-action-row" onClick={() => edit(sheetUnit)}><span className="pm-action-ico"><EditOutlined /></span>Modifier la {t.unitSingular}</button>}
            {can.create && <button className="pm-action-row" onClick={() => duplicate(sheetUnit)}><span className="pm-action-ico"><CopyOutlined /></span>Dupliquer</button>}
            {can.delete && <button className="pm-action-row is-danger" onClick={() => askDelete(sheetUnit)}><span className="pm-action-ico"><DeleteOutlined /></span>Supprimer</button>}
          </div>
        </>
      )}
    </div>
  );
}

function Header({ t, onBack, onMenu, onSearch, onBell }) {
  return (
    <MobileModuleHeader
      title={t.unitsTitle}
      subtitle={`Touchez une ${t.unitSingular} pour la produire ou la modifier`}
      onMenu={onMenu}
      onLeading={onMenu ? undefined : onBack}
      leadingIcon="arrow-left"
      leadingLabel="Retour"
      actions={
        <>
          {onSearch ? (
            <MobileHeaderAction label={`Rechercher une ${t.unitSingular}`} onClick={onSearch}>
              <SearchOutlined />
            </MobileHeaderAction>
          ) : null}
          <MobileHeaderAction label="Notifications" onClick={onBell}>
            <BellOutlined />
          </MobileHeaderAction>
        </>
      }
    />
  );
}

function KindPill({ kind }) {
  const fin = kind !== SEMI;
  return <span className={`pm-pill pm-pill--sm ${fin ? "pm-pill--blue" : "pm-pill--violet"}`}>{fin ? "Produit fini" : "Semi-fini"}</span>;
}
